import { Prose, Unavailable, Method, CourseLink } from "./Bits";
import { EvidenceChip } from "../EvidenceChip";
import { OutcomeTile } from "../OutcomeTile";
import { CompareTable } from "../CompareTable";
import { Explorer } from "./Explorer";

export function Careers({ d }) {
  const byUni = d.employers.reduce((a, e) => ((a[e.university] = a[e.university] || []).push(e), a), {});
  return (
    <div className="space-y-12">
      <div className="space-y-10">
        <div className="max-w-4xl"><h3 className="mb-4 text-sm font-semibold text-stone-900">Across the UK</h3>
          {d.editorial.careers_national.length ? <div className="space-y-4">{d.editorial.careers_national.map((it, i) => (
            <Prose key={i} item={it} testId={`sci-national-career-${i}`} className={i === 0 ? "font-editorial text-xl leading-relaxed text-stone-800" : "text-base leading-relaxed text-stone-800"} />))}</div>
            : <Unavailable testId="sci-national-career-none">Not verified yet.</Unavailable>}</div>
        <div><h3 className="mb-4 text-sm font-semibold text-stone-900">What the universities report</h3>
          <ul className="grid gap-3 md:grid-cols-2">{d.editorial.careers_universities.map((it, i) => (
            <li key={i} className="rounded-xl border border-stone-200 bg-white p-4"><Prose item={it} testId={`sci-subject-career-${i}`} className="text-sm leading-relaxed text-stone-800" /></li>))}</ul></div>
      </div>
      <div>
        <h3 className="mb-1 text-sm font-semibold text-stone-900">Named employers, only where a university publishes them</h3>
        {Object.keys(byUni).length ? Object.entries(byUni).map(([u, rows]) => (
          <div key={u} className="mt-3" data-testid="sci-named-employers">
            <p className="text-xs text-amber-900">{u} careers service: destinations of its Philosophy graduates (this subject at this university, not all Philosophy graduates)</p>
            <div className="mt-2 flex flex-wrap gap-2">{rows.map((e, i) => (
              <span key={i} data-testid={`sci-named-employer-${i}`} className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white py-1 pl-3 pr-1 text-sm">{e.value}<EvidenceChip claim={e} subtle testId={`sci-named-employer-${i}-source`} /></span>))}</div>
          </div>)) : <Unavailable testId="sci-named-employers-none">No university in the pilot publishes named employers.</Unavailable>}
        <Unavailable testId="sci-employers-gap">Named employers for Oxford, Edinburgh and Berkeley Philosophy graduates aren't published on their official pages, so we don't show any.</Unavailable>
      </div>
    </div>
  );
}

export function Roi({ roi }) {
  return (
    <div className="space-y-12">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-stone-900">Subject level: all Philosophy graduates</h3>
        {roi.national.length ? <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{roi.national.map((o, i) => <OutcomeTile key={i} o={o} testId={`sci-roi-national-${i}`} />)}</div>
          : <Unavailable testId="sci-roi-national-none">Not yet verified.</Unavailable>}
      </div>
      <div>
        <h3 className="mb-3 text-sm font-semibold text-stone-900">At four leading courses</h3>
        <div className="space-y-6">{roi.courses.map((c) => (
          <div key={c.degree_id} data-testid={`sci-roi-course-${c.degree_id}`}>
            <p className="mb-2 text-sm"><CourseLink id={c.degree_id} testId={`sci-roi-link-${c.degree_id}`}>{c.university}: {c.title}</CourseLink>
              {c.joint && <span className="ml-2 text-xs text-amber-900">Earnings are for each subject group at Oxford, not PPE itself</span>}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {c.outcomes.filter((o) => !o.subject_tab || /philosoph/i.test(o.subject_tab)).filter((o) => /earnings|work_or_study/.test(o.field)).slice(0, 3).map((o, i) => <OutcomeTile key={i} o={o} testId={`sci-roi-${c.degree_id}-${i}`} />)}
              {c.fees.map((f, i) => (
                <div key={`f${i}`} data-testid={`sci-roi-fee-${c.degree_id}-${i}`} className="rounded-xl border border-stone-200 bg-white p-4">
                  <div className="flex justify-between gap-2"><p className="text-xs text-stone-600">Tuition fee</p><EvidenceChip claim={f} subtle testId={`sci-roi-fee-${c.degree_id}-${i}-source`} /></div>
                  <p className="mt-1 font-mono text-xl">{f.value}</p><p className="text-[11px] text-stone-500">{f.context}</p>
                </div>))}
              {!c.fees.length && <Unavailable testId={`sci-roi-fee-none-${c.degree_id}`}>Tuition not yet verified.</Unavailable>}
            </div>
          </div>))}</div>
      </div>
      <ul className="list-disc space-y-1 pl-5 text-xs text-stone-600" data-testid="sci-roi-caveats">
        <li>UK figures come from Discover Uni (Graduate Outcomes at 15 months; LEO tax data at 3 and 5 years for older cohorts). US figures come from College Scorecard and cover federally aided completers only.</li>
        <li>Starting and 5-year figures come from different cohorts, so they don't show one group's pay rising.</li>
        <li>Earnings aren't adjusted for inflation, region or further study. Small samples can swing a lot.</li>
        <li>No salary or employer list here is attributed to a course unless the source reports it for that course.</li>
      </ul>
    </div>
  );
}

export function WhereToStudy({ d }) {
  return (
    <div className="space-y-14">
      <Explorer />
      <div data-testid="sci-verified-four">
        <h3 className="text-sm font-semibold text-stone-900">Four courses we've verified in depth</h3>
        <p className="mb-4 mt-1 text-xs text-stone-500">A research pilot, not a ranking or a top four. Every cell comes from the official course page.</p>
        <CompareTable courses={d.courses} />
      </div>
    </div>
  );
}
